import { buildRoomNumberingHeatmapEdges, buildRoomNumberingHeatmapNodes, } from "./guiMethods";
import { buildRoomAdjacencyFromGrid, buildRoomNumberingAdjacency, buildRoomNumberingGrid, classifyRoom, } from "./roomNumberingMethods";
import { assignRoomNumbers, buildStaircaseDistanceMap, } from "./corridorOrderingMethods";
import { assignCorridorNumbers, assignElevatorNumbers, assignShaftNumbers, assignStaircaseNumbers, assignTechnicalRoomNumbers, } from "./specialRoomTypes";
function toCalculationRoom(room) {
    return {
        building: room.building,
        polygon: room.polygon,
        roomName: room.roomName,
        roomNumber: room.roomNumber,
        manualRoomNumber: room.manualRoomNumber,
        approved: room.approved,
    };
}
function toFloorRooms(floor) {
    return {
        floorIndex: floor.floorIndex,
        floorName: floor.floorName,
        rooms: floor.rooms.map(toCalculationRoom),
        calculatedNumbers: {},
    };
}
function floorKey(floor) {
    return floor.floorGlobalId ?? floor.floorName ?? `floorIndex:${floor.floorIndex}`;
}
function roomCentroid(room) {
    const pts = room.polygon;
    const count = pts.length || 1;
    return [
        pts.reduce((sum, point) => sum + point[0], 0) / count,
        pts.reduce((sum, point) => sum + point[1], 0) / count,
    ];
}
export const calculateNrhRoomNumbers = ({ currentFloor, allFloors, doors = [], restrictionLines = [], numberingOrigin = null, }) => {
    const roomsOnFloor = currentFloor.rooms.map(toCalculationRoom);
    const grid = buildRoomNumberingGrid(roomsOnFloor, currentFloor.floorIndex);
    const adj = buildRoomNumberingAdjacency(grid, roomsOnFloor, doors, restrictionLines);
    const staircases = roomsOnFloor.filter((room) => classifyRoom(room.roomName) === "staircase");
    let originStair = null;
    if (numberingOrigin != null) {
        const [originX, originY] = numberingOrigin;
        let nearestDist = Infinity;
        for (const stair of staircases) {
            const [centerX, centerY] = roomCentroid(stair);
            const dist = (centerX - originX) ** 2 + (centerY - originY) ** 2;
            if (dist < nearestDist) {
                nearestDist = dist;
                originStair = stair;
            }
        }
    }
    else {
        let minProjection = Infinity;
        for (const stair of staircases) {
            const [centerX, centerY] = roomCentroid(stair);
            const projection = centerX + centerY;
            if (projection < minProjection) {
                minProjection = projection;
                originStair = stair;
            }
        }
    }
    const roomsForDijkstra = originStair == null
        ? roomsOnFloor
        : roomsOnFloor.map((room) => classifyRoom(room.roomName) === "staircase" && room !== originStair
            ? { ...room, roomName: undefined }
            : room);
    const { distMap, penaltyCostMap } = buildStaircaseDistanceMap(grid, adj, roomsForDijkstra);
    const roomAdjTable = buildRoomAdjacencyFromGrid(grid);
    const roomNumbersBase = assignRoomNumbers(grid, adj, distMap, penaltyCostMap, roomsOnFloor, doors, roomAdjTable, currentFloor.floorIndex);
    const floorsByKey = new Map();
    for (const floor of allFloors) {
        floorsByKey.set(floorKey(floor), toFloorRooms(floor));
    }
    const currentFloorForSpecialRooms = toFloorRooms(currentFloor);
    floorsByKey.set(floorKey(currentFloor), currentFloorForSpecialRooms);
    const allFloorsForSpecialRooms = [...floorsByKey.values()];
    const specialNumbers = {
        ...assignStaircaseNumbers(currentFloorForSpecialRooms, allFloorsForSpecialRooms, grid, distMap),
        ...assignElevatorNumbers(currentFloorForSpecialRooms, allFloorsForSpecialRooms),
        ...assignShaftNumbers(currentFloorForSpecialRooms, allFloorsForSpecialRooms),
        ...assignCorridorNumbers(currentFloorForSpecialRooms, grid, distMap),
        ...assignTechnicalRoomNumbers(currentFloorForSpecialRooms),
    };
    return {
        roomNumberToCalculatedRoomNumber: {
            ...roomNumbersBase,
            ...specialNumbers,
        },
        heatmap: {
            edges: buildRoomNumberingHeatmapEdges(grid, distMap),
            nodes: buildRoomNumberingHeatmapNodes(grid, distMap),
        },
    };
};
